/**
 * Where a signed-in person belongs.
 *
 * An owner belongs on the dashboard: every location, every figure, the whole
 * business. Somebody who works one counter does not — they belong at that
 * counter, with the two or three things they do there.
 *
 * Until now everyone landed on `/` regardless, so accepting an invitation as a
 * courier dropped you into a shop manager's screen: Products, Receiving,
 * Customers, Staff, Reports. Nothing was leaking that their role did not
 * already allow, but it was the wrong job's tools, and the one they actually
 * needed was not on the list.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';

interface MyLocations {
  canSeeAll: boolean;
  defaultBranchId: string | null;
  branches: Array<{ id: string; name: string }>;
}

export type WorkerHome =
  /** Still working it out — render nothing rather than the wrong screen. */
  | { state: 'loading' }
  /** Not a worker. The dashboard is theirs. */
  | { state: 'dashboard' }
  /** One location: straight to work. */
  | { state: 'workspace'; branchId: string }
  /** Several: they pick, because the app must not guess where somebody is. */
  | { state: 'choose'; branches: Array<{ id: string; name: string }> }
  /** A worker nobody has assigned yet. */
  | { state: 'unassigned' };

/**
 * Decided by ROLE, not by how many locations somebody happens to have.
 *
 * An owner restricted to one location for a week is still an owner and still
 * wants their dashboard. A worker with three locations is still a worker. Using
 * the location count would make the experience flip about as assignments
 * changed, which is not a thing anybody could explain to a new hire.
 */
export function useWorkerHome(): WorkerHome {
  const { business } = useAuth();
  // A location login is a worker in this sense, with exactly one location.
  const isWorker = business?.role === 'worker' || business?.role === 'location_operator';

  const mine = useQuery({
    queryKey: ['branches-mine', business?.businessId],
    queryFn: () =>
      api.get<MyLocations>(`/businesses/${business!.businessId}/branches/mine`),
    enabled: Boolean(business) && isWorker,
  });

  if (!business) return { state: 'loading' };
  if (!isWorker) return { state: 'dashboard' };
  if (mine.isPending) return { state: 'loading' };

  const branches = mine.data?.branches ?? [];
  if (branches.length === 0) return { state: 'unassigned' };
  if (branches.length === 1) return { state: 'workspace', branchId: branches[0]!.id };
  return { state: 'choose', branches };
}
